import { useTranslation } from "@repo/i18n";

/**
 * Party-size stepper capped at the service max, with the large-party request path ONE step above it
 * (R1/SC-5): `value` may reach `max + 1`, the "grande tablée" sentinel that renders `{max}+` and flips
 * the search to the request path — a party above max is NEVER a slot query. The live region announces
 * the value on change; the buttons are ≥44px touch targets and keyboard-operable.
 */
export function PartyStepper({
  value,
  max,
  onChange,
  labelId,
}: {
  value: number;
  max: number;
  onChange: (next: number) => void;
  labelId: string;
}) {
  const { t } = useTranslation();
  const isLarge = value > max;
  const display = isLarge ? `${max}+` : String(value);

  return (
    <div>
      <span id={labelId} className="rsv-label">
        {t("widget.booking.search.party")}
      </span>
      <div className="rsv-stepper" aria-labelledby={labelId}>
        <button
          type="button"
          onClick={() => onChange(Math.max(1, value - 1))}
          disabled={value <= 1}
          aria-label={t("widget.booking.search.partyDecrease")}
        >
          <span aria-hidden="true">−</span>
        </button>
        <span className="rsv-stepper-value" aria-live="polite" data-testid="party-value">
          {display}
        </span>
        <button
          type="button"
          onClick={() => onChange(Math.min(max + 1, value + 1))}
          disabled={value >= max + 1}
          aria-label={t("widget.booking.search.partyIncrease")}
        >
          <span aria-hidden="true">+</span>
        </button>
      </div>
      {isLarge ? (
        <p className="rsv-hint">{t("widget.booking.search.largeParty", { max })}</p>
      ) : null}
    </div>
  );
}
